import { useEffect, useMemo, useRef, useState } from "react";
import type { WorkspaceTeamBlueprint } from "@yagni/shared";
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CheckCircle2,
  Plug,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  UsersRound,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  FormField,
  Input,
  ProgressBar,
  Textarea,
} from "../ui/index.js";
import { readJsonError } from "../lib/readJsonError.js";
import { ConfidenceBadge, EvidencePanel, TeamCard } from "./companyTeamOnboarding/BlueprintPanels.js";
import {
  CONNECTION_OPTIONS,
  MAX_ENABLED_TEAMS,
  connectorLabel,
  draftFromBlueprint,
  requestPayload,
  restoreStoredDraft,
  teamBlueprintDraftStorageKey,
  validateDraft,
  type CompanyProfileDraft,
  type RequestPhase,
  type TeamDraft,
} from "./companyTeamOnboarding/model.js";

type BlueprintWriteResponse = { teamBlueprint: WorkspaceTeamBlueprint };
type BlueprintInstallResponse = {
  blueprint: WorkspaceTeamBlueprint;
  createdTeamIds: string[];
  waitingConnectionIdsByTeam: Record<string, string[]>;
};

type ActivationSummary = Pick<
  BlueprintInstallResponse,
  "blueprint" | "waitingConnectionIdsByTeam"
>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isConfirmedInstalledBlueprint(value: unknown): value is WorkspaceTeamBlueprint {
  if (!isRecord(value) || value.status !== "installed" || !Array.isArray(value.teams)) return false;
  if (!isRecord(value.installedTeamIds)) return false;
  const candidateIds = new Set(
    value.teams.flatMap((team) =>
      isRecord(team) && typeof team.id === "string" ? [team.id] : [],
    ),
  );
  return Object.entries(value.installedTeamIds).some(
    ([candidateId, installedTeamId]) =>
      candidateIds.has(candidateId) &&
      typeof installedTeamId === "string" &&
      installedTeamId.length > 0,
  );
}

function stringArrayRecord(value: unknown): Record<string, string[]> {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.entries(value).flatMap(([key, entry]) =>
      Array.isArray(entry) && entry.every((item) => typeof item === "string")
        ? [[key, entry as string[]]]
        : [],
    ),
  );
}

function joinLabels(labels: readonly string[]): string {
  if (labels.length < 2) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
}

function activationConnectionSummary(
  team: WorkspaceTeamBlueprint["teams"][number],
  waitingConnectionIdsByTeam: Readonly<Record<string, string[]>>,
): string {
  const expected = team.expectedConnectorIds;
  if (expected.length === 0) return "No Connection needed";

  if (!Object.hasOwn(waitingConnectionIdsByTeam, team.id)) {
    return `Review Connections after continuing: ${joinLabels(expected.map(connectorLabel))}.`;
  }

  const waiting = new Set(waitingConnectionIdsByTeam[team.id] ?? []);
  const connected = expected.filter((id) => !waiting.has(id)).map(connectorLabel);
  const personal = expected
    .filter((id) => waiting.has(id) && CONNECTION_OPTIONS.some((option) => option.id === id && option.personal))
    .map(connectorLabel);
  const needed = expected
    .filter((id) => waiting.has(id) && !CONNECTION_OPTIONS.some((option) => option.id === id && option.personal))
    .map(connectorLabel);
  const parts: string[] = [];
  if (connected.length > 0) parts.push(`Connected: ${joinLabels(connected)}.`);
  if (needed.length > 0) parts.push(`Waiting for: ${joinLabels(needed)}.`);
  if (personal.length > 0) parts.push(`Choose personal access after install: ${joinLabels(personal)}.`);
  return parts.join(" ");
}

/**
 * The researched company as a read-first dossier (onboarding 1B): label/value
 * rows separated by hairline rules, prose fields in the serif voice. Empty
 * fields simply don't appear — the recognition moment reads as a crisp brief,
 * not a form with blanks.
 */
function CompanyProfileRead({ profile }: { profile: CompanyProfileDraft }) {
  const rows: { label: string; value: string; serif?: boolean; strong?: boolean }[] = [
    // Company is required, so its row never disappears: a cleared name reads
    // "Not set yet" here, keeping the Create-time validation error pointed at
    // something visible on the page.
    { label: "Company", value: profile.legalOrBrandName.trim() || "Not set yet", strong: true },
    { label: "Industry", value: [profile.industry, profile.stageOrSize].filter(Boolean).join(" · ") },
    { label: "One-line read", value: profile.oneLiner, serif: true },
    { label: "What you do", value: profile.whatTheyDo, serif: true },
    { label: "Who you serve", value: profile.whoTheyServe, serif: true },
    { label: "Business model", value: profile.businessModel },
    { label: "Products", value: profile.products },
    { label: "Customer segments", value: profile.customerSegments },
    { label: "Operating priorities", value: profile.operatingPriorities },
  ].filter((row) => row.value.trim().length > 0);

  if (rows.length === 0) {
    return (
      <p className="text-sm text-[var(--text-tertiary)]">
        We could not verify company details from public sources. Add them with Edit company, or teach the Teams later.
      </p>
    );
  }

  return (
    <dl data-testid="company-profile-read">
      {rows.map((row, index) => (
        <div
          key={row.label}
          className={index === 0 ? "pb-3" : "border-t border-[var(--border-subtle)] py-3 last:pb-0"}
        >
          <dt className="text-xs text-[var(--text-tertiary)]">{row.label}</dt>
          <dd
            className={
              row.strong
                ? "mt-0.5 text-[15px] font-semibold text-[var(--text-primary)]"
                : row.serif
                  ? "mt-0.5 whitespace-pre-line font-[var(--font-serif)] text-[15px] leading-6 text-[var(--text-secondary)]"
                  : "mt-0.5 whitespace-pre-line text-[15px] text-[var(--text-primary)]"
            }
          >
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function TeamRoomActivationSummary({
  summary,
  onContinue,
}: {
  summary: ActivationSummary;
  onContinue: () => void;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const installedTeams = summary.blueprint.teams.filter(
    (team) => Boolean(summary.blueprint.installedTeamIds[team.id]),
  );

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="flex h-[var(--tri-stage-h)] min-h-0 flex-col overflow-hidden [background:var(--canvas-field)] text-[var(--text-primary)]">
      <header className="shrink-0 border-b border-[var(--border)] bg-[color-mix(in_srgb,var(--paper-card)_92%,transparent)] px-5 py-5 backdrop-blur md:px-8 lg:px-10">
        <div className="mx-auto max-w-[1180px]">
          <p role="status" aria-live="polite" className="sr-only">Teams created.</p>
          <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--accent)]">
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            Teams created
          </div>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="font-[var(--font-display)] text-2xl font-semibold tracking-[-0.03em] md:text-3xl"
          >
            Your Teams are ready
          </h1>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-[var(--text-secondary)] md:text-base">
            {installedTeams.length} {installedTeams.length === 1 ? "Team was" : "Teams were"} created with every line supervised. Review what is connected now and what still needs you.
          </p>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto grid max-w-[1180px] items-start gap-6 px-5 py-6 md:px-8 lg:grid-cols-[0.72fr_1.28fr] lg:gap-8 lg:px-10 lg:py-8">
          <section aria-labelledby="activated-teams-title" className="lg:col-start-2 lg:row-start-1">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--accent)]">03 · Activate</p>
                <h2 id="activated-teams-title" className="mt-1 font-[var(--font-display)] text-xl font-semibold">Created Teams</h2>
              </div>
              <span className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                <UsersRound className="h-4 w-4 text-[var(--accent)]" aria-hidden="true" />
                {installedTeams.length} created
              </span>
            </div>
            <ul className="space-y-3">
              {installedTeams.map((team) => {
                return (
                  <li key={team.id}>
                    <Card padding="md" className="bg-[var(--paper-card)]">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-[var(--font-display)] text-base font-semibold">{team.recipe.teamName} Team</h3>
                            <Badge variant="warning" size="xs">Supervised</Badge>
                          </div>
                          <p className="mt-1 text-xs leading-5 text-[var(--text-secondary)]">
                            {team.recipe.responsibilities[0] ?? team.rationale}
                          </p>
                        </div>
                        <ShieldCheck className="h-4 w-4 text-[var(--accent)]" aria-hidden="true" />
                      </div>
                      <p className="mt-3 flex items-start gap-2 border-t border-[var(--border-subtle)] pt-3 text-xs leading-5 text-[var(--text-secondary)]">
                        <Plug className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--accent)]" aria-hidden="true" />
                        {activationConnectionSummary(team, summary.waitingConnectionIdsByTeam)}
                      </p>
                    </Card>
                  </li>
                );
              })}
            </ul>
          </section>

          <section aria-labelledby="activation-next-title" className="space-y-4 lg:col-start-1 lg:row-start-1">
            <Card padding="lg" className="border-[var(--accent-border,var(--border))] bg-[var(--accent-subtle)]">
              <Sparkles className="h-5 w-5 text-[var(--accent)]" aria-hidden="true" />
              <h2 id="activation-next-title" className="mt-3 font-[var(--font-display)] text-lg font-semibold">What happens next</h2>
              <p className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">
                Starter Work is being prepared as proposals in Work. Nothing ships without your approval.
              </p>
              <p className="mt-3 text-sm leading-6 text-[var(--text-secondary)]">
                Missing Connections stay as requests. Personal inboxes and calendars are never attached automatically.
              </p>
            </Card>
            <Button type="button" className="h-10 w-full" onClick={onContinue}>
              Continue to Work
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </section>
        </div>
      </main>
    </div>
  );
}

/**
 * The claim review is inserted only before Work. Deferred plans require a
 * deliberate resume click; they never hold Work on their own.
 */
export function shouldShowCompanyTeamOnboarding(
  pathname: string,
  role: string,
  godMode: boolean,
  blueprint: WorkspaceTeamBlueprint | null | undefined,
  resumeRequested = false,
): boolean {
  if (pathname !== "/team-room/setup" || godMode || !blueprint) return false;
  if (role !== "owner" && role !== "admin") return false;
  if (
    blueprint.status === "draft" ||
    blueprint.status === "failed" ||
    blueprint.status === "approved"
  ) return true;
  return blueprint.status === "deferred" && resumeRequested;
}

interface CompanyTeamOnboardingProps {
  initialBlueprint: WorkspaceTeamBlueprint;
  onBlueprintChange: (blueprint: WorkspaceTeamBlueprint) => void;
  onDeferred: (blueprint: WorkspaceTeamBlueprint) => void;
  onInstalled: (blueprint: WorkspaceTeamBlueprint) => void;
}

export function CompanyTeamOnboarding({
  initialBlueprint,
  onBlueprintChange,
  onDeferred,
  onInstalled,
}: CompanyTeamOnboardingProps) {
  const [blueprint, setBlueprint] = useState(initialBlueprint);
  const [draft, setDraft] = useState(() => {
    const stored = typeof window === "undefined"
      ? null
      : window.sessionStorage.getItem(teamBlueprintDraftStorageKey(initialBlueprint.id));
    return restoreStoredDraft(initialBlueprint, stored) ?? draftFromBlueprint(initialBlueprint);
  });
  const [expandedTeamIds, setExpandedTeamIds] = useState<Set<string>>(() =>
    initialBlueprint.status === "approved"
      ? new Set(initialBlueprint.teams.map((team) => team.id))
      : new Set(),
  );
  const [phase, setPhase] = useState<RequestPhase>("idle");
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [requestMessage, setRequestMessage] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [activationSummary, setActivationSummary] = useState<ActivationSummary | null>(null);
  const mountedRef = useRef(true);
  const alertRef = useRef<HTMLDivElement>(null);

  // The company column is read-first (onboarding 1B): the researched profile
  // renders as a dossier, and the form only appears on "Edit company". Cancel
  // restores the snapshot taken when editing began. A snapshot is only valid
  // for the draft it was taken from: whenever the draft is wholesale replaced
  // by fresher server data (a reload after a 409, a successful save, a new
  // blueprint) the edit session closes and the snapshot is dropped — otherwise
  // Cancel would overwrite the newer data with the stale pre-reload copy.
  const [editingCompany, setEditingCompany] = useState(false);
  const companySnapshotRef = useRef<CompanyProfileDraft | null>(null);
  const closeCompanyEdit = () => {
    companySnapshotRef.current = null;
    setEditingCompany(false);
  };

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  useEffect(() => {
    setBlueprint(initialBlueprint);
    const stored = typeof window === "undefined"
      ? null
      : window.sessionStorage.getItem(teamBlueprintDraftStorageKey(initialBlueprint.id));
    setDraft(restoreStoredDraft(initialBlueprint, stored) ?? draftFromBlueprint(initialBlueprint));
    if (initialBlueprint.status === "approved") {
      setExpandedTeamIds(new Set(initialBlueprint.teams.map((team) => team.id)));
    }
    setActivationSummary(null);
    setConflict(false);
    companySnapshotRef.current = null;
    setEditingCompany(false);
  }, [initialBlueprint.id, initialBlueprint.revision]);

  useEffect(() => {
    if (validationMessage || requestMessage) alertRef.current?.focus();
  }, [requestMessage, validationMessage]);

  // Keyboard focus follows the company edit session: entering lands on the
  // first field, leaving returns to the "Edit company" control, so the swap
  // between dossier and form never drops focus to <body>. Transition-only (a
  // prev-value ref) so mounting never steals focus.
  const prevEditingCompanyRef = useRef(editingCompany);
  useEffect(() => {
    if (prevEditingCompanyRef.current === editingCompany) return;
    prevEditingCompanyRef.current = editingCompany;
    const target = editingCompany
      ? document.getElementById("company-name")
      : document.getElementById("company-edit-toggle");
    target?.focus();
  }, [editingCompany]);

  const enabledCount = draft.teams.filter((team) => team.enabled).length;
  const busy = phase !== "idle";
  const approvedResume = blueprint.status === "approved";
  const canonicalDraft = useMemo(() => draftFromBlueprint(blueprint), [blueprint]);
  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(canonicalDraft),
    [canonicalDraft, draft],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const key = teamBlueprintDraftStorageKey(blueprint.id);
    if (dirty) {
      window.sessionStorage.setItem(key, JSON.stringify({ revision: blueprint.revision, draft }));
    } else {
      window.sessionStorage.removeItem(key);
    }
  }, [blueprint.id, blueprint.revision, dirty, draft]);

  useEffect(() => {
    if (!dirty || busy || typeof window === "undefined") return;
    const preventAccidentalReload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", preventAccidentalReload);
    return () => window.removeEventListener("beforeunload", preventAccidentalReload);
  }, [busy, dirty]);

  const editCompany = <Key extends keyof CompanyProfileDraft>(
    key: Key,
    value: CompanyProfileDraft[Key],
  ) => {
    setDraft((current) => ({
      ...current,
      companyProfile: { ...current.companyProfile, [key]: value },
    }));
  };

  const beginCompanyEdit = () => {
    companySnapshotRef.current = { ...draft.companyProfile };
    setEditingCompany(true);
  };
  const cancelCompanyEdit = () => {
    const snapshot = companySnapshotRef.current;
    if (snapshot) {
      setDraft((current) => ({ ...current, companyProfile: snapshot }));
    }
    closeCompanyEdit();
  };

  const editTeam = (id: string, patch: Partial<TeamDraft>) => {
    setDraft((current) => ({
      ...current,
      teams: current.teams.map((team) => (team.id === id ? { ...team, ...patch } : team)),
    }));
  };

  const toggleTeam = (id: string) => {
    const target = draft.teams.find((team) => team.id === id);
    if (!target) return;
    setValidationMessage(null);
    if (!target.enabled && enabledCount >= MAX_ENABLED_TEAMS) {
      setValidationMessage("You can enable up to five Teams during setup.");
      return;
    }
    if (target.enabled && enabledCount === 1) {
      setValidationMessage("Keep at least one Team enabled.");
      return;
    }
    setDraft((current) => {
      const teams = current.teams.map((team) =>
        team.id === id ? { ...team, enabled: !team.enabled } : team,
      );
      const nextEnabled = teams.filter((team) => team.enabled);
      const primaryTeamId = nextEnabled.some((team) => team.id === current.primaryTeamId)
        ? current.primaryTeamId
        : nextEnabled[0]!.id;
      return { ...current, teams, primaryTeamId };
    });
  };

  const reloadLatest = async (announce = true): Promise<WorkspaceTeamBlueprint | null> => {
    if (announce) setPhase("reloading");
    try {
      const response = await fetch("/api/workspace/onboarding", { credentials: "include" });
      if (!response.ok) throw new Error("reload_failed");
      const body = (await response.json()) as { teamBlueprint?: WorkspaceTeamBlueprint | null };
      if (!body.teamBlueprint) throw new Error("blueprint_missing");
      setBlueprint(body.teamBlueprint);
      setDraft(draftFromBlueprint(body.teamBlueprint));
      closeCompanyEdit();
      setConflict(false);
      if (announce) setRequestMessage(null);
      onBlueprintChange(body.teamBlueprint);
      return body.teamBlueprint;
    } catch {
      if (announce) {
        setRequestMessage("We couldn't reload the latest Team plan. Check your connection and try again.");
      }
      return null;
    } finally {
      if (announce) setPhase("idle");
    }
  };

  const saveCurrentDraft = async (): Promise<WorkspaceTeamBlueprint | null> => {
    const saveResponse = await fetch("/api/workspace/onboarding/team-blueprint", {
      method: "PATCH",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(requestPayload(blueprint, draft)),
    });
    if (!saveResponse.ok) {
      // Every conflict shape from this endpoint rides a 409 status.
      setConflict(saveResponse.status === 409);
      setRequestMessage(await readJsonError(saveResponse));
      return null;
    }
    const saveBody = (await saveResponse.json().catch(() => ({}))) as unknown;

    const saved = {
      ...(saveBody as BlueprintWriteResponse).teamBlueprint,
      connectedConnectorIds: blueprint.connectedConnectorIds ?? [],
    };
    setBlueprint(saved);
    setDraft(draftFromBlueprint(saved));
    closeCompanyEdit();
    return saved;
  };

  const createRoom = async () => {
    const validation = approvedResume ? null : validateDraft(draft);
    setValidationMessage(validation);
    setRequestMessage(null);
    setConflict(false);
    if (validation) return;

    try {
      let saved = blueprint;
      if (!approvedResume) {
        setPhase("saving");
        const updated = await saveCurrentDraft();
        if (!updated) {
          setPhase("idle");
          return;
        }
        saved = updated;
      }

      setPhase("installing");

      const installResponse = await fetch("/api/workspace/onboarding/team-blueprint/install", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ expectedRevision: saved.revision }),
      });
      if (!installResponse.ok) {
        setConflict(installResponse.status === 409);
        setRequestMessage(`${await readJsonError(installResponse)} Your reviewed plan is safe.`);
        setPhase("idle");
        await reloadLatest(false);
        return;
      }
      const installBody = (await installResponse.json().catch(() => ({}))) as unknown;

      const result = installBody as Partial<BlueprintInstallResponse>;
      const installed = result.blueprint;
      if (!isConfirmedInstalledBlueprint(installed)) {
        setPhase("idle");
        setRequestMessage("The Teams may have been created, but we couldn't confirm their setup summary. Try again to load it safely.");
        return;
      }
      if (!mountedRef.current) return;
      setPhase("idle");
      setBlueprint(installed);
      setDraft(draftFromBlueprint(installed));
      closeCompanyEdit();
      window.sessionStorage.removeItem(teamBlueprintDraftStorageKey(blueprint.id));
      setActivationSummary({
        blueprint: installed,
        waitingConnectionIdsByTeam: stringArrayRecord(result.waitingConnectionIdsByTeam),
      });
    } catch {
      setPhase("idle");
      setRequestMessage("We lost the connection while creating your Teams. Your reviewed plan is safe; try again.");
    }
  };

  const defer = async () => {
    const validation = validateDraft(draft);
    setValidationMessage(validation);
    if (validation) return;
    setPhase("deferring");
    setRequestMessage(null);
    setConflict(false);
    try {
      const saved = await saveCurrentDraft();
      if (!saved) return;
      const response = await fetch("/api/workspace/onboarding/team-blueprint/defer", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ expectedRevision: saved.revision }),
      });
      if (!response.ok) {
        setConflict(response.status === 409);
        setRequestMessage(await readJsonError(response));
        return;
      }
      const body = (await response.json().catch(() => ({}))) as unknown;
      const deferred = (body as BlueprintWriteResponse).teamBlueprint;
      if (!mountedRef.current) return;
      setBlueprint(deferred);
      window.sessionStorage.removeItem(teamBlueprintDraftStorageKey(blueprint.id));
      onDeferred(deferred);
    } catch {
      setRequestMessage("We couldn't defer the Team plan. Check your connection and try again.");
    } finally {
      setPhase("idle");
    }
  };

  const phaseCopy = phase === "saving"
    ? "Saving your reviewed plan…"
    : phase === "installing"
      ? "Creating your Teams…"
      : phase === "deferring"
        ? "Leaving the plan ready for later…"
        : phase === "reloading"
          ? "Reloading the latest plan…"
          : null;
  const progress = phase === "saving" ? 38 : phase === "installing" ? 76 : 18;

  if (activationSummary) {
    return (
      <TeamRoomActivationSummary
        summary={activationSummary}
        onContinue={() => onInstalled(activationSummary.blueprint)}
      />
    );
  }

  return (
    <div className="flex h-[var(--tri-stage-h)] min-h-0 flex-col overflow-hidden [background:var(--canvas-field)] text-[var(--text-primary)]">
      <header className="shrink-0 border-b border-[var(--border)] bg-[color-mix(in_srgb,var(--paper-card)_92%,transparent)] px-5 py-4 backdrop-blur md:px-8 lg:px-10">
        <div className="mx-auto flex max-w-[1480px] flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-1 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--text-tertiary)]">
              <Sparkles className="h-3.5 w-3.5 text-[var(--accent)]" aria-hidden="true" />
              Researched from {blueprint.companyProfile.domain}
            </div>
            <h1 className="font-[var(--font-display)] text-2xl font-semibold tracking-[-0.03em] md:text-3xl">
              Build your Teams
            </h1>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-[var(--text-secondary)] md:text-base">
              {blueprint.teams.length > 1
                ? `We prepared ${blueprint.teams.length} Teams from the evidence we found. Review them, then create the ones you want. You can create more Teams later.`
                : "We could only prepare one Team confidently this run. You can create more Teams later."}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-full border border-[var(--border)] bg-[var(--paper-card)] px-3 py-1.5 text-sm text-[var(--text-secondary)]">
              <strong className="text-[var(--text-primary)]">{enabledCount}</strong> of {draft.teams.length} enabled
            </div>
            {/* A single-Team plan is the planner's degraded fallback; dressing it
                in a confidence badge would be dishonest (onboarding 1B). */}
            {blueprint.teams.length > 1 && (
              <ConfidenceBadge confidence={blueprint.companyProfile.confidence} />
            )}
          </div>
        </div>
      </header>

      <nav aria-label="Teams review sections" className="shrink-0 border-b border-[var(--border-subtle)] bg-[var(--paper-card)] px-5 md:px-8 lg:hidden">
        <div className="mx-auto flex max-w-[1480px]">
          <a className="flex flex-1 items-center justify-center gap-2 border-b-2 border-[var(--accent)] px-3 py-3 text-sm font-medium" href="#company-profile">
            <span className="grid h-5 w-5 place-items-center rounded-full bg-[var(--accent)] text-xs text-white">1</span>
            Company profile
          </a>
          <a className="flex flex-1 items-center justify-center gap-2 border-b-2 border-transparent px-3 py-3 text-sm font-medium text-[var(--text-secondary)]" href="#team-room">
            <span className="grid h-5 w-5 place-items-center rounded-full bg-[var(--border-subtle)] text-xs">2</span>
            Teams
          </a>
        </div>
      </nav>

      <div className="min-h-0 flex-1 overflow-y-auto scroll-smooth motion-reduce:scroll-auto">
        <fieldset disabled={busy || approvedResume} className="contents">
        <main className="mx-auto grid max-w-[1480px] items-start gap-6 px-5 py-6 md:px-8 lg:grid-cols-[minmax(320px,0.78fr)_minmax(600px,1.45fr)] lg:gap-8 lg:px-10 lg:py-8">
          <section id="company-profile" aria-labelledby="company-profile-title" className="min-w-0 scroll-mt-6">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--accent)]">01 · Company</p>
                <h2 id="company-profile-title" className="mt-1 font-[var(--font-display)] text-xl font-semibold tracking-[-0.02em]">
                  The company we found
                </h2>
              </div>
              <Building2 className="mt-1 h-5 w-5 text-[var(--text-tertiary)]" aria-hidden="true" />
            </div>

            {editingCompany ? (
              <Card padding="lg" className="space-y-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField label="Company name" htmlFor="company-name" required>
                    <Input
                      id="company-name"
                      value={draft.companyProfile.legalOrBrandName}
                      maxLength={120}
                      onChange={(event) => editCompany("legalOrBrandName", event.target.value)}
                    />
                  </FormField>
                  <FormField label="Industry" htmlFor="company-industry">
                    <Input
                      id="company-industry"
                      value={draft.companyProfile.industry}
                      maxLength={160}
                      onChange={(event) => editCompany("industry", event.target.value)}
                    />
                  </FormField>
                </div>
                <FormField label="One-line read" htmlFor="company-one-liner">
                  <Textarea
                    id="company-one-liner"
                    autosize
                    rows={2}
                    value={draft.companyProfile.oneLiner}
                    maxLength={500}
                    onChange={(event) => editCompany("oneLiner", event.target.value)}
                  />
                </FormField>
                <FormField label="What you do" htmlFor="company-what">
                  <Textarea
                    id="company-what"
                    autosize
                    rows={3}
                    value={draft.companyProfile.whatTheyDo}
                    maxLength={2000}
                    onChange={(event) => editCompany("whatTheyDo", event.target.value)}
                  />
                </FormField>
                <FormField label="Who you serve" htmlFor="company-who">
                  <Textarea
                    id="company-who"
                    autosize
                    rows={2}
                    value={draft.companyProfile.whoTheyServe}
                    maxLength={1000}
                    onChange={(event) => editCompany("whoTheyServe", event.target.value)}
                  />
                </FormField>
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField label="Stage or size" htmlFor="company-stage">
                    <Input
                      id="company-stage"
                      value={draft.companyProfile.stageOrSize}
                      maxLength={500}
                      onChange={(event) => editCompany("stageOrSize", event.target.value)}
                    />
                  </FormField>
                  <FormField label="Business model" htmlFor="company-model">
                    <Input
                      id="company-model"
                      value={draft.companyProfile.businessModel}
                      maxLength={500}
                      onChange={(event) => editCompany("businessModel", event.target.value)}
                    />
                  </FormField>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField label="Products" htmlFor="company-products">
                    <Textarea
                      id="company-products"
                      autosize
                      rows={2}
                      value={draft.companyProfile.products}
                      onChange={(event) => editCompany("products", event.target.value)}
                    />
                  </FormField>
                  <FormField label="Customer segments" htmlFor="company-segments">
                    <Textarea
                      id="company-segments"
                      autosize
                      rows={2}
                      value={draft.companyProfile.customerSegments}
                      onChange={(event) => editCompany("customerSegments", event.target.value)}
                    />
                  </FormField>
                </div>
                <FormField label="Operating priorities" htmlFor="company-priorities">
                  <Textarea
                    id="company-priorities"
                    autosize
                    rows={3}
                    value={draft.companyProfile.operatingPriorities}
                    onChange={(event) => editCompany("operatingPriorities", event.target.value)}
                  />
                </FormField>
                <div className="flex items-center gap-2 pt-1">
                  <Button type="button" size="sm" onClick={closeCompanyEdit}>
                    Save
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={cancelCompanyEdit}>
                    Cancel
                  </Button>
                </div>
              </Card>
            ) : (
              <Card padding="lg">
                <CompanyProfileRead profile={draft.companyProfile} />
                <div className="mt-4">
                  <Button id="company-edit-toggle" type="button" variant="ghost" size="sm" onClick={beginCompanyEdit}>
                    Edit company
                  </Button>
                </div>
              </Card>
            )}

            <EvidencePanel profile={blueprint.companyProfile} />
          </section>

          <section id="team-room" aria-labelledby="team-room-title" className="min-w-0 scroll-mt-6">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--accent)]">02 · Teams</p>
                <h2 id="team-room-title" className="mt-1 font-[var(--font-display)] text-xl font-semibold tracking-[-0.02em]">
                  Your proposed Teams
                </h2>
                <p className="mt-1 text-sm text-[var(--text-secondary)]">
                  {blueprint.teams.length > 1
                    ? "The strongest recommendations are ready. Enable up to five and choose which one leads activation."
                    : "We could prepare one Team this run. It is fully editable before creation."}
                </p>
              </div>
              <div className="flex items-center gap-2 rounded-lg border border-[var(--accent-border,var(--border))] bg-[var(--accent-muted)] px-3 py-2 text-xs text-[var(--text-secondary)]">
                <ShieldCheck className="h-4 w-4 text-[var(--accent)]" aria-hidden="true" />
                Every Team starts in Training
              </div>
            </div>

            {(validationMessage || requestMessage) && (
              <div
                role="alert"
                ref={alertRef}
                tabIndex={-1}
                className="mb-4 flex items-start justify-between gap-4 rounded-lg border border-[var(--warning)]/30 bg-[var(--warning-muted)] px-4 py-3 text-sm text-[var(--text-primary)]"
              >
                <span className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--warning)]" aria-hidden="true" />
                  {validationMessage ?? requestMessage}
                </span>
                {conflict && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    loading={phase === "reloading"}
                    onClick={() => void reloadLatest()}
                    className="shrink-0"
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                    Reload latest plan
                  </Button>
                )}
              </div>
            )}

            {blueprint.status === "failed" && !requestMessage && (
              <div className="mb-4 rounded-lg border border-[var(--warning)]/30 bg-[var(--warning-muted)] px-4 py-3 text-sm">
                Creation paused on the last attempt. The plan is intact—review it and try again.
              </div>
            )}
            {approvedResume && !requestMessage && (
              <div className="mb-4 rounded-lg border border-[var(--accent-border,var(--border))] bg-[var(--accent-muted)] px-4 py-3 text-sm">
                The plan is approved. Creation was interrupted before the Teams finished; resume from the exact saved version below.
              </div>
            )}

            <div className="space-y-3">
              {blueprint.teams.map((candidate) => {
                const teamDraft = draft.teams.find((team) => team.id === candidate.id)!;
                const expanded = expandedTeamIds.has(candidate.id);
                return (
                  <TeamCard
                    key={candidate.id}
                    candidate={candidate}
                    draft={teamDraft}
                    expanded={expanded}
                    onToggle={() => toggleTeam(candidate.id)}
                    onExpand={() => {
                      setExpandedTeamIds((current) => {
                        const next = new Set(current);
                        if (next.has(candidate.id)) next.delete(candidate.id);
                        else next.add(candidate.id);
                        return next;
                      });
                    }}
                    onChange={(patch) => editTeam(candidate.id, patch)}
                    connectedConnectorIds={blueprint.connectedConnectorIds}
                  />
                );
              })}
            </div>

            <div className="mt-4 flex items-start gap-2 rounded-lg border border-[var(--border)] bg-[var(--paper-card)] px-4 py-3 text-xs leading-5 text-[var(--text-secondary)]">
              <Plug className="mt-0.5 h-4 w-4 shrink-0 text-[var(--accent)]" aria-hidden="true" />
              <p>
                Shared Connections already active in this workspace are attached only when selected here. Missing Connections stay as requests, and the Teams can still be created. Personal inboxes and calendars are never attached automatically.
              </p>
            </div>
          </section>
        </main>
        </fieldset>
      </div>

      <footer className="shrink-0 border-t border-[var(--border)] bg-[color-mix(in_srgb,var(--paper-card)_94%,transparent)] px-5 py-3 backdrop-blur md:px-8 lg:px-10">
        {/* Single row, never wraps: the status text truncates and the action
            group is reserved space, so the Create button cannot be pushed
            below the fixed-height stage and clipped (onboarding 1B). */}
        <div className="mx-auto flex max-w-[1480px] items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            {phaseCopy ? (
              <div role="status" aria-live="polite" className="max-w-md">
                <div className="mb-1.5 flex items-center justify-between gap-3 text-xs font-medium text-[var(--text-secondary)]">
                  <span>{phaseCopy}</span>
                  <span>{phase === "installing" ? "Building your Teams" : "Keeping the plan safe"}</span>
                </div>
                <ProgressBar value={progress} />
              </div>
            ) : (
              <div className="min-w-0">
                <p className="truncate text-sm text-[var(--text-secondary)]">
                  <strong className="text-[var(--text-primary)]">{enabledCount} Teams</strong> · every line supervised · no Connection required
                </p>
                <p className="mt-0.5 hidden truncate text-xs text-[var(--text-tertiary)] sm:block">
                  Creating applies the reviewed Playbook rules and files Starter Work as proposals for your approval.
                </p>
              </div>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              disabled={busy || approvedResume}
              loading={phase === "deferring"}
              onClick={() => void defer()}
            >
              Review later
            </Button>
            <Button
              type="button"
              disabled={busy}
              loading={phase === "saving" || phase === "installing"}
              onClick={() => void createRoom()}
              className="min-w-[148px] whitespace-nowrap"
            >
              {approvedResume ? "Finish creating" : "Create"} {enabledCount} {enabledCount === 1 ? "Team" : "Teams"}
              {!busy && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
}
